/**
 * Methodology Connections Service
 *
 * Provides typed CRUD wrappers for the methodology_connections table,
 * which links consulting clarity artifacts (outcome drivers, back plan
 * nodes, OST nodes, open questions) to prioritization domain objects
 * (items, cycles, sessions, tracks).
 *
 * Connection creation goes through the create_methodology_connection
 * RPC to prevent duplicates atomically.
 */

import { getSupabaseClient } from './supabaseClient';

// ── Types ─────────────────────────────────────────────────────

export type MethodologySourceType =
  | 'outcome_driver'
  | 'desired_outcome'
  | 'backplan_node'
  | 'roadmap_node'
  | 'ost_node'
  | 'open_question'
  | 'objective'
  | 'goal';

export type MethodologyTargetType =
  | 'item'
  | 'cycle'
  | 'session'
  | 'track';

export type MethodologyConnectionType =
  | 'informs'
  | 'motivates'
  | 'addresses'
  | 'answers'
  | 'derives_from';

export interface MethodologyConnection {
  id: string;
  workspaceId: string;
  sourceEntityType: MethodologySourceType;
  sourceEntityId: string;
  targetEntityType: MethodologyTargetType;
  targetEntityId: string;
  connectionType: MethodologyConnectionType;
  createdBy: string | null;
  createdAt: string;
}

export interface CreateConnectionInput {
  workspaceId: string;
  sourceEntityType: MethodologySourceType;
  sourceEntityId: string;
  targetEntityType: MethodologyTargetType;
  targetEntityId: string;
  connectionType?: MethodologyConnectionType;
  createdBy?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────

function rowToConnection(r: Record<string, unknown>): MethodologyConnection {
  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    sourceEntityType: r.source_entity_type as MethodologySourceType,
    sourceEntityId: r.source_entity_id as string,
    targetEntityType: r.target_entity_type as MethodologyTargetType,
    targetEntityId: r.target_entity_id as string,
    connectionType: (r.connection_type as MethodologyConnectionType) ?? 'informs',
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
  };
}

// ── Service functions ─────────────────────────────────────────

/**
 * Create a methodology connection via RPC.
 * The RPC deduplicates atomically and returns the id (new or existing).
 */
export async function createConnection(
  input: CreateConnectionInput
): Promise<{ data: MethodologyConnection | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('create_methodology_connection', {
      p_workspace_id: input.workspaceId,
      p_source_entity_type: input.sourceEntityType,
      p_source_entity_id: input.sourceEntityId,
      p_target_entity_type: input.targetEntityType,
      p_target_entity_id: input.targetEntityId,
      p_connection_type: input.connectionType ?? 'informs',
      p_created_by: input.createdBy ?? null,
    });

    if (error) return { data: null, error: error.message };

    const result = data as { id?: string; error?: string } | null;
    if (result?.error) return { data: null, error: String(result.error) };
    if (!result?.id) return { data: null, error: 'RPC returned no id' };

    // Fetch the full row
    const { data: row, error: fetchError } = await supabase
      .from('methodology_connections')
      .select('*')
      .eq('id', result.id)
      .single();

    if (fetchError) return { data: null, error: fetchError.message };
    return { data: rowToConnection(row as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: String(err) };
  }
}

/**
 * Fetch all methodology connections where the target is a specific domain object.
 * e.g. "What clarity artifacts inform item X?"
 */
export async function getConnectionsForTarget(
  targetEntityType: MethodologyTargetType,
  targetEntityId: string
): Promise<{ data: MethodologyConnection[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('methodology_connections')
      .select('*')
      .eq('target_entity_type', targetEntityType)
      .eq('target_entity_id', targetEntityId)
      .order('created_at', { ascending: true });

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((r) => rowToConnection(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: String(err) };
  }
}

/**
 * Fetch all methodology connections where the source is a specific clarity artifact.
 * e.g. "What items does outcome driver Y inform?"
 */
export async function getConnectionsForSource(
  sourceEntityType: MethodologySourceType,
  sourceEntityId: string
): Promise<{ data: MethodologyConnection[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('methodology_connections')
      .select('*')
      .eq('source_entity_type', sourceEntityType)
      .eq('source_entity_id', sourceEntityId)
      .order('created_at', { ascending: true });

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((r) => rowToConnection(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: String(err) };
  }
}

/**
 * Delete a methodology connection by id.
 */
export async function deleteConnection(
  id: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('methodology_connections').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true, error: null };
  } catch (err) {
    return { success: false, error: String(err) };
  }
}

/**
 * Fetch all connections for a workspace grouped by source type.
 * Useful for workspace-level clarity maps.
 */
export async function getConnectionsForWorkspace(
  workspaceId: string
): Promise<{ data: MethodologyConnection[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('methodology_connections')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };
    return {
      data: (data ?? []).map((r) => rowToConnection(r as Record<string, unknown>)),
      error: null,
    };
  } catch (err) {
    return { data: [], error: String(err) };
  }
}
